/**
 * features/exams/InvigilationPane.tsx — the whole rota, as one list.
 * ------------------------------------------------------------------
 * The office's own spreadsheet, with the two things it cannot do for itself:
 *
 *   IT KNOWS THE TIMETABLE. A name is offered with the lessons that duty
 *   would empty ("Form 3B Physics, periods 2–3"), not a bare "unavailable".
 *   The check is per DUTY WINDOW, so a key-time supervisor released at 09:00
 *   is judged on 08:00–09:00 and nothing else.
 *
 *   IT KNOWS THE LOAD. Duties per person across the series, so "everyone has
 *   had about the same" is a figure rather than an argument.
 *
 * EVERY PAPER IS ON ONE PAGE (the school, 2026-09-23). It used to show one
 * session at a time behind a dropdown and a pair of arrows, so answering
 * "who is invigilating on the 8th?" meant clicking through the series, and
 * the concerns lived in a summary card that named a session you then had to
 * go and find. Now every paper is a row — its invigilators, its key time and
 * its own concerns in place — and the filters narrow the list instead of the
 * arrows moving it. A concern is stated ON the row it belongs to, because a
 * warning that is somewhere else is a warning nobody acts on.
 *
 * EVERY ROW IS EDITABLE IN PLACE. Names come off with the × beside them and
 * go on through the picker, which is opened on the row itself. The edit is
 * shown at once and written behind it; a refused write puts the server's row
 * back.
 *
 * THE PICKER OFFERS ONLY THOSE WHO ARE FREE (the school, 2026-09-23) —
 * nobody teaching a lesson in that window and nobody owed in another room at
 * the same moment. The rest are still reachable behind one labelled toggle,
 * because rule 2 below is the school's own and says the exam officer may
 * choose them anyway; what changed is that they are no longer the default,
 * mixed in with everybody else.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, CalendarDays, CheckCircle2, Clock, Filter, Plus, Printer,
  ShieldCheck, UserPlus, Users, X,
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, updateDoc } from '@/lib/firebase';
import { SCHOOL } from '@/lib/school.config';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import {
  PanelHeader, SoftCard, StatChip, StatStrip, btnGhost, glassFieldClass,
} from '@/components/ui/design';
import {
  availabilityFor, clashesFor, dayLoad, dayOf, dutyTally, isFree, lessonsThatRun, papersBySession, staffingProblems,
  type DutyAvailability, type DutyClash, type DutyRole, type ExamDuty, type ExamPaperDuties,
  type SchoolPhase,
  type PeriodTimes,
} from '@/lib/examDuties';
import type { TimetableSlot } from '@/lib/timetable';
import { useExamDuties, periodTimesFor } from './useExamDuties';
import { ROLE_ICON, ROLE_NAME_CHIP } from './dutyStyle';
import { shortDay } from './ExamsHub';
import { loadTimetable } from './useExamTimetable';

interface Person {
  id: string;
  name: string;
  /** Which part of the school they work in; empty when nobody has said. */
  phases: ReadonlySet<SchoolPhase>;
}
type Tally = ReturnType<typeof dutyTally>;

/** A paper, with everything the row needs already worked out once. */
interface Row {
  paper: ExamPaperDuties;
  periods: PeriodTimes[];
  concerns: string[];
  /** Which part of the school sits it — null when it is not known. */
  phase: SchoolPhase | null;
}

export default function InvigilationPane({ seriesId }: { seriesId: string }) {
  const { permissions, userData } = useAuth();
  const { teachers } = useData();
  const {
    papers: loaded, slots, schedule, teachesPaper, phaseOf, phasesOfTeacher, internal, loading, error,
  } = useExamDuties(seriesId);

  /* Heads of department do not invigilate the school's own exams (rule 5).
     Who is a HOD is a role on `users`, which only admins read, so it comes
     from a callable that returns teacher-record ids and nothing else. */
  const [hodIds, setHodIds] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    if (!internal) return undefined;
    let live = true;
    httpsCallable<unknown, { teacherIds?: string[] }>(functions, 'getHodTeacherIds')()
      .then((r) => { if (live) setHodIds(new Set(r.data.teacherIds ?? [])); })
      .catch(() => { /* the list is then offered whole */ });
    return () => { live = false; };
  }, [internal]);

  /* Edited in place: the screen shows the change at once and the document is
     written behind it. A refused write puts the server's row back. */
  const [papers, setPapers] = useState<ExamPaperDuties[]>([]);
  useEffect(() => { setPapers(loaded); }, [loaded]);

  const [find, setFind] = useState('');
  const [onlyConcerns, setOnlyConcerns] = useState(false);
  const [fromToday, setFromToday] = useState(true);
  const [saving, setSaving] = useState('');
  const [printing, setPrinting] = useState(false);
  const [saveError, setSaveError] = useState('');

  const sessions = useMemo(() => papersBySession(papers), [papers]);
  const problems = useMemo(() => staffingProblems(papers, teachesPaper), [papers, teachesPaper]);
  const tally = useMemo(() => dutyTally(papers), [papers]);
  const people = useMemo<Person[]>(
    /* fullName, not displayName: the display name drops the initial, and two
       teachers share a surname (Mr T. Dube, Ms S. Dube). It is also what the
       imported rota stored, so a chip and the picker read the same. */
    () => (teachers ?? []).map((t) => ({
      id: t.id,
      name: t.fullName || t.displayName || t.lastName,
      phases: phasesOfTeacher(t.id),
    })),
    [teachers, phasesOfTeacher],
  );

  /* Each paper's own concerns, phrased for the row they sit on rather than
     for a summary card — the row already says which paper it is. */
  const concernsByPaper = useMemo(() => {
    const byId = new Map(papers.map((p) => [p.id, p]));
    const out = new Map<string, string[]>();
    const push = (id: string, text: string) => out.set(id, [...(out.get(id) ?? []), text]);
    for (const p of problems) {
      if (p.kind === 'unstaffed') push(p.paperId, 'Nobody is invigilating this paper.');
      else if (p.kind === 'soleSubjectTeacher') {
        push(p.paperId, `${p.teacherName} teaches this subject and is the only invigilator — a second is needed.`);
      } else {
        const other = byId.get(p.otherPaperId);
        push(p.paperId, `${p.teacherName} is also on ${other ? `${other.code} ${other.subject}` : 'another paper'} at the same time.`);
      }
    }
    return out;
  }, [problems, papers]);

  /* Bell times are per weekday, so they are worked out once per DATE rather
     than once per paper — 89 papers over a fortnight is a dozen days. */
  const periodsByDate = useMemo(() => {
    const out = new Map<string, PeriodTimes[]>();
    for (const s of sessions) {
      if (!out.has(s.date)) out.set(s.date, periodTimesFor(schedule, dayOf(s.date)));
    }
    return out;
  }, [sessions, schedule]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const visible = useMemo(() => {
    const needle = find.trim().toLowerCase();
    return sessions
      .map((s) => ({
        ...s,
        rows: s.papers
          .map<Row>((paper) => ({
            paper,
            periods: periodsByDate.get(paper.date) ?? [],
            concerns: concernsByPaper.get(paper.id) ?? [],
            phase: phaseOf(paper),
          }))
          .filter((r) => {
            if (onlyConcerns && !r.concerns.length) return false;
            if (!needle) return true;
            const hay = [r.paper.code, r.paper.subject, r.paper.component, shortDay(r.paper.date),
              ...r.paper.duties.map((d) => d.teacherName)].join(' ').toLowerCase();
            return hay.includes(needle);
          }),
      }))
      .filter((s) => s.rows.length && (!fromToday || s.date >= today));
  }, [sessions, periodsByDate, concernsByPaper, phaseOf, onlyConcerns, find, fromToday, today]);

  const write = async (paper: ExamPaperDuties, duties: ExamDuty[]) => {
    const before = papers;
    setPapers((rows) => rows.map((r) => (r.id === paper.id ? { ...r, duties } : r)));
    setSaving(paper.id);
    setSaveError('');
    try {
      await updateDoc(doc(db, 'reportExamDuties', paper.id), {
        duties,
        updatedAt: new Date().toISOString(),
        updatedBy: userData?.uid ?? '',
      });
      /* WHO MAY FILL THE REGISTER follows the rota (2026-10-29). It used to be
         set only by seed-exam-registers.cjs, so a duty given here never let
         that teacher take the register, and internal exams are staffed here
         alone. A register covers every room of one paper (same code, date and
         session), so it takes everyone on any of them. */
      const sameSitting = papers.map((r) => (r.id === paper.id ? { ...r, duties } : r))
        .filter((r) => r.code === paper.code && r.date === paper.date && r.session === paper.session);
      const invigilatorIds = [...new Set(sameSitting.flatMap((r) => r.duties.map((d) => d.teacherId)).filter(Boolean))];
      const registerId = `${seriesId}__${paper.date}__${paper.session}__${paper.code.replace(/\//g, '-')}`;
      await updateDoc(doc(db, 'reportExamRegisters', registerId), { invigilatorIds }).catch(() => undefined);
    } catch (e) {
      setPapers(before);
      setSaveError(e instanceof Error ? e.message : 'That change was not saved.');
    } finally {
      setSaving('');
    }
  };

  /* The office's printed sheet (the school, 2026-10-06). The duty documents do
     not say a paper's level or the syllabus's full name, the Cambridge
     timetable does, so it is read on the click along with jsPDF. */
  async function printSheet(list: ExamPaperDuties[]) {
    setPrinting(true);
    setSaveError('');
    try {
      const [{ buildInvigilationPdf }, sheet, seriesSnap] = await Promise.all([
        import('@/lib/invigilationPdf'),
        import('@/lib/invigilationSheet'),
        getDoc(doc(db, 'reportExamSeries', seriesId)),
      ]);
      const timetableId = seriesSnap.exists() ? String(seriesSnap.data().timetableId ?? '') : '';
      const internal = seriesSnap.exists() && seriesSnap.data().kind === 'internal';
      const seriesTitle = seriesSnap.exists() ? String(seriesSnap.data().title ?? '') : '';
      const timetable = timetableId ? await loadTimetable(timetableId) : null;
      const byCode = new Map((timetable?.components ?? []).map((c) => [c.code, c]));
      const LEVEL: Record<string, string> = { IGCSE: 'IGCSE', AS: 'AS', 'A Level': 'A LEVEL' };
      const qualificationOf = (code: string, paper: ExamPaperDuties) => {
        // An internal paper names the form that sits it (the school, 2026-10-30):
        // one code is sat by several forms (0475 in Forms 1 to 4), each its own paper.
        if (internal) return typeof paper.form === 'number' ? `FORM ${paper.form}` : 'SCHOOL';
        const level = byCode.get(code)?.level;
        if (level) return LEVEL[level] ?? level.toUpperCase();
        return code.startsWith('9') ? 'AS & A LEVEL' : 'IGCSE';
      };
      const rows = sheet.sheetRows(list, qualificationOf, (code) => byCode.get(code)?.subject ?? '');
      // Al-Nur renumbers its internal sessions 1 and 2. LKC's Examinations Unit
      // prints AM / MM / PM, so the sheet keeps the session as it is.
      const levels = [...new Set(rows.map((r) => r.cells[0]))];
      const titleLevels = levels.some((l) => l !== 'IGCSE')
        ? (levels.includes('IGCSE') ? 'IGCSE, AS & A LEVEL' : 'AS & A LEVEL')
        : 'IGCSE';
      const pdf = buildInvigilationPdf({
        rows,
        seriesLine: internal ? seriesTitle.toUpperCase() : `CAMBRIDGE INTERNATIONAL EXAMINATIONS - ${sheet.seriesMonth(seriesId)}`,
        titleLine: internal ? 'INVIGILATION TIMETABLE' : `INVIGILATION TIMETABLE FOR ${titleLevels} EXAMINATIONS`,
        printedOn: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
        // No FCS or key-time supervision in the school's own exams (rule 5).
        dropColumns: internal ? sheet.KEY_TIME_COLUMNS : [],
        showCentre: !internal,
      });
      pdf.save(`invigilation-timetable-${seriesId}.pdf`);
    } catch (e) {
      setSaveError(e instanceof Error ? `The timetable could not be printed: ${e.message}` : 'The timetable could not be printed.');
    } finally {
      setPrinting(false);
    }
  }

  /* The DAY SHEETS (the school's option A of 2026-10-06, in blue and gold):
     a day per half page, morning beside afternoon, for pigeonholes. */
  async function printDaySheets(list: ExamPaperDuties[]) {
    setPrinting(true);
    setSaveError('');
    try {
      const [{ buildDaySheetsPdf }, sheet, seriesSnap] = await Promise.all([
        import('@/lib/invigilationDaySheetsPdf'),
        import('@/lib/invigilationSheet'),
        getDoc(doc(db, 'reportExamSeries', seriesId)),
      ]);
      const title = seriesSnap.exists() ? String(seriesSnap.data().title ?? '') : '';
      const headerLine = internal
        ? `${SCHOOL.name.toUpperCase()}  ·  ${title.toUpperCase()}  ·  INVIGILATION`
        : `${SCHOOL.name.toUpperCase()} - ${SCHOOL.cambridgeCentre}  ·  CAMBRIDGE ${sheet.seriesMonth(seriesId)}  ·  INVIGILATION`;
      buildDaySheetsPdf({ papers: list, headerLine, internal }).save(`invigilation-day-sheets-${seriesId}.pdf`);
    } catch (e) {
      setSaveError(e instanceof Error ? `The day sheets could not be printed: ${e.message}` : 'The day sheets could not be printed.');
    } finally {
      setPrinting(false);
    }
  }

  if (loading) return <p className="py-10 text-sm text-[var(--sp-muted)]">Loading the rota…</p>;
  if (error) return <SoftCard><p className="text-sm text-rose-600 dark:text-rose-400">{error}</p></SoftCard>;
  if (!papers.length) {
    return (
      <SoftCard pad="">
        <p className="px-5 py-10 text-center text-sm text-[var(--sp-muted)]">
          No invigilation rota has been imported for this series yet.
        </p>
      </SoftCard>
    );
  }

  const withConcerns = concernsByPaper.size;
  /* A concern the filters have hidden is a concern nobody acts on, which is
     the fault this screen was rebuilt to fix — so if the date filter is
     covering one, say so rather than leaving the header count unexplained. */
  const hiddenConcerns = fromToday
    ? papers.filter((p) => concernsByPaper.has(p.id) && p.date < today).length
    : 0;
  /* Off the tally, so the figure means what the school means by a duty: a
     session in a room, however many papers are sat in it concurrently. */
  const duties = tally.reduce((n, t) => n + t.duties, 0);
  const shown = visible.reduce((n, s) => n + s.rows.length, 0);

  return (
    <div className="space-y-5">
      <StatStrip>
        <StatChip tone="navy" icon={CalendarDays} value={papers.length} label={`papers across ${sessions.length} sessions`} />
        <StatChip tone="sky" icon={Users} value={duties} label={`duties across ${tally.length} staff`} />
        <StatChip
          tone={withConcerns ? 'rose' : 'emerald'}
          icon={withConcerns ? AlertTriangle : CheckCircle2}
          value={withConcerns}
          label={withConcerns ? 'papers with something to settle' : 'every paper is staffed and clear'}
        />
        <StatChip
          tone="amber"
          value={tally[0]?.duties ?? 0}
          label={tally[0] ? `most duties — ${tally[0].teacherName}` : 'nobody on the rota'}
        />
      </StatStrip>

      {saveError && (
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300">
          {saveError}
        </p>
      )}

      <SoftCard pad="">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-[var(--sp-line)] px-4 py-3">
          <input
            value={find}
            onChange={(e) => setFind(e.target.value)}
            placeholder="Find a paper, subject or invigilator"
            className={glassFieldClass('w-auto min-w-[16rem] flex-1')}
            aria-label="Find a paper, subject or invigilator"
          />
          <label className="inline-flex items-center gap-2 text-sm text-[var(--sp-body)]">
            <input
              type="checkbox" checked={onlyConcerns} onChange={(e) => setOnlyConcerns(e.target.checked)}
              className="h-4 w-4 accent-rose-600"
            />
            <span className="inline-flex items-center gap-1.5">
              <Filter className="h-3.5 w-3.5" /> Only papers with something to settle
            </span>
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-[var(--sp-body)]">
            <input
              type="checkbox" checked={fromToday} onChange={(e) => setFromToday(e.target.checked)}
              className="h-4 w-4 accent-sky-600"
            />
            Hide days already sat
          </label>
          <p className="ml-auto text-xs tabular-nums text-[var(--sp-muted)]">
            {shown} of {papers.length} papers
          </p>
          <button
            type="button"
            className={btnGhost}
            disabled={!shown || printing}
            title="The school's invigilation timetable, for the papers listed below"
            onClick={() => void printSheet(visible.flatMap((s) => s.rows.map((r) => r.paper)))}
          >
            <Printer className="h-4 w-4" /> {printing ? 'Preparing…' : 'Print timetable'}
          </button>
          <button
            type="button"
            className={btnGhost}
            disabled={!shown || printing}
            title="A sheet per day, morning beside afternoon, with who reports when: for pigeonholes"
            onClick={() => void printDaySheets(visible.flatMap((s) => s.rows.map((r) => r.paper)))}
          >
            <Printer className="h-4 w-4" /> Print day sheets
          </button>
        </div>

        {!!hiddenConcerns && (
          <p className="border-b border-[var(--sp-line)] bg-amber-50/70 px-4 py-2 text-xs text-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
            {hiddenConcerns} paper{hiddenConcerns === 1 ? '' : 's'} with something to settle
            {hiddenConcerns === 1 ? ' is' : ' are'} on a day already sat — untick “Hide days already sat” to see
            {hiddenConcerns === 1 ? ' it' : ' them'}.
          </p>
        )}

        {!shown && (
          <p className="px-4 py-10 text-center text-sm text-[var(--sp-muted)]">
            No paper matches those filters.
          </p>
        )}

        {visible.map((session) => (
          <section key={session.key}>
            <h3 className="sticky top-0 z-10 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-y border-[var(--sp-line)] bg-[var(--sp-paper)] px-4 py-2">
              <span className="text-sm font-bold text-ink dark:text-slate-100">
                {shortDay(session.date)}
                <span className="ml-2 font-semibold text-[var(--sp-body)]">{session.session || 'session'}</span>
              </span>
              <span className="text-xs text-[var(--sp-muted)]">
                {session.rows.length} paper{session.rows.length === 1 ? '' : 's'}
                {periodsByDate.get(session.date)?.length
                  ? ''
                  : ' · no bell times for this day, so lessons cannot be checked'}
              </span>
            </h3>
            <div className="divide-y divide-[var(--sp-line)]">
              {session.rows.map((row) => (
                <PaperDuties
                  key={row.paper.id}
                  row={row}
                  papers={papers}
                  slots={slots}
                  people={people}
                  tally={tally}
                  teachesPaper={teachesPaper}
                  internal={internal}
                  hodIds={hodIds}
                  canEdit={permissions.canManageExamTimetables}
                  busy={saving === row.paper.id}
                  onChange={(duties) => { void write(row.paper, duties); }}
                />
              ))}
            </div>
          </section>
        ))}
      </SoftCard>

      <Load tally={tally} internal={internal} />
    </div>
  );
}

/* ── one paper ────────────────────────────────────────────────────── */

function PaperDuties({ row, papers, slots: allSlots, people, tally, teachesPaper, internal, hodIds, canEdit, busy, onChange }: {
  row: Row;
  papers: ExamPaperDuties[];
  slots: TimetableSlot[];
  people: Person[];
  tally: Tally;
  teachesPaper: (teacherId: string, paper: ExamPaperDuties) => boolean;
  internal: boolean;
  hodIds: ReadonlySet<string>;
  canEdit: boolean;
  busy: boolean;
  onChange: (duties: ExamDuty[]) => void;
}) {
  const { paper, periods, concerns } = row;
  const [adding, setAdding] = useState<DutyRole | null>(null);
  /* A form sitting one of the school's own exams has no lessons then, so its
     teachers are free (rule 5). Cambridge papers carry no form and change
     nothing. */
  const slots = useMemo(() => lessonsThatRun(allSlots, papers, paper.date, periods), [allSlots, papers, paper.date, periods]);
  const keyTime = paper.duties.filter((d) => d.role === 'keyTime');
  const invigilators = paper.duties.filter((d) => d.role === 'invigilator');

  const add = (person: Person, role: DutyRole) => {
    /* A new name takes the window the others on that role are working, which
       for a key-time supervisor is NOT the paper's own. */
    const like = role === 'keyTime' ? keyTime[0] : invigilators[0];
    onChange([...paper.duties, {
      teacherId: person.id,
      teacherName: person.name,
      role,
      from: like?.from || paper.start,
      to: like?.to || paper.end,
    }]);
    setAdding(null);
  };

  return (
    <div className={`px-4 py-4 ${busy ? 'opacity-60' : ''} ${concerns.length ? 'bg-rose-50/40 dark:bg-rose-950/10' : ''}`}>
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h4 className="text-sm font-bold text-ink dark:text-slate-100">
          {paper.code} <span className="font-semibold text-[var(--sp-body)]">{paper.subject}</span>
          {paper.component && <span className="font-normal text-[var(--sp-muted)]"> · {paper.component}</span>}
        </h4>
        <p className="inline-flex items-center gap-1.5 text-xs text-[var(--sp-muted)]">
          <Clock className="h-3.5 w-3.5" />
          {paper.start && paper.end ? `${paper.start} – ${paper.end}` : 'time not recorded'}
          {paper.duration ? ` · ${paper.duration}` : ''} · {paper.entries} candidate{paper.entries === 1 ? '' : 's'}
        </p>
      </header>

      {/* The concern sits ON the paper it is about — the whole reason the
          screen stopped being one session at a time. */}
      {concerns.map((text) => (
        <p key={text} className="mt-2 inline-flex items-start gap-1.5 text-sm font-semibold text-rose-700 dark:text-rose-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {text}
        </p>
      ))}

      {/* No FCS or key-time supervision in the school's own exams (rule 5). */}
      {(!internal || keyTime.length > 0) && <DutyRow
        label="FCS · key time" role="keyTime" icon={ShieldCheck} duties={keyTime} paper={paper} slots={slots} periods={periods}
        teachesPaper={teachesPaper} canEdit={canEdit}
        onRemove={(duty) => onChange(paper.duties.filter((d) => d !== duty))}
        onAdd={() => setAdding('keyTime')}
        empty="nobody is supervising the window around this paper"
      />}
      <DutyRow
        label="Invigilators" role="invigilator" icon={Users} duties={invigilators} paper={paper} slots={slots} periods={periods}
        teachesPaper={teachesPaper} canEdit={canEdit}
        onRemove={(duty) => onChange(paper.duties.filter((d) => d !== duty))}
        onAdd={() => setAdding('invigilator')}
        empty="nobody is in the room for this paper"
        emptyTone="rose"
      />

      {adding && canEdit && (
        <Picker
          role={adding}
          paper={paper}
          paperPhase={row.phase}
          papers={papers}
          people={people}
          tally={tally}
          slots={slots}
          periods={periods}
          teachesPaper={teachesPaper}
          internal={internal}
          hodIds={hodIds}
          onPick={(person) => add(person, adding)}
          onClose={() => setAdding(null)}
        />
      )}
    </div>
  );
}

function DutyRow({ label, role, icon: Icon, duties, paper, slots, periods, teachesPaper, canEdit, onRemove, onAdd, empty, emptyTone }: {
  label: string;
  /** What this row IS — FCS carries its own colour, on both exam screens. */
  role: DutyRole;
  icon: typeof Users;
  duties: ExamDuty[];
  paper: ExamPaperDuties;
  slots: TimetableSlot[];
  periods: PeriodTimes[];
  teachesPaper: (teacherId: string, paper: ExamPaperDuties) => boolean;
  canEdit: boolean;
  onRemove: (duty: ExamDuty) => void;
  onAdd: () => void;
  empty: string;
  emptyTone?: 'rose';
}) {
  return (
    <div className="mt-3 flex flex-wrap items-start gap-x-3 gap-y-2">
      <p className={`inline-flex w-28 shrink-0 items-center gap-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wide ${
        role === 'keyTime' ? ROLE_ICON.keyTime : 'text-[var(--sp-muted)]'}`}>
        <Icon className="h-3.5 w-3.5" /> {label}
      </p>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        {duties.map((duty, i) => (
          <DutyChip
            key={`${duty.teacherId}-${i}`}
            duty={duty}
            paper={paper}
            clashes={clashesFor(duty.teacherId, { date: paper.date, from: duty.from, to: duty.to }, slots, periods)}
            teachesSubject={teachesPaper(duty.teacherId, paper)}
            canEdit={canEdit}
            onRemove={() => onRemove(duty)}
          />
        ))}
        {!duties.length && (
          <p className={`text-sm ${emptyTone === 'rose' ? 'font-semibold text-rose-600 dark:text-rose-400' : 'text-[var(--sp-muted)]'}`}>
            {empty}
          </p>
        )}
        {canEdit && (
          <button type="button" onClick={onAdd} className={`${btnGhost} px-2.5 py-1 text-xs`}>
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        )}
      </div>
    </div>
  );
}

function DutyChip({ duty, paper, clashes, teachesSubject, canEdit, onRemove }: {
  duty: ExamDuty;
  paper: ExamPaperDuties;
  clashes: DutyClash[];
  teachesSubject: boolean;
  canEdit: boolean;
  onRemove: () => void;
}) {
  const ownWindow = duty.from !== paper.start || duty.to !== paper.end;
  return (
    <span className={`inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2.5 py-1 text-sm ${ROLE_NAME_CHIP[duty.role]}`}>
      <span className="truncate font-semibold text-ink dark:text-slate-100">{duty.teacherName}</span>
      {ownWindow && (
        <span className="whitespace-nowrap text-xs tabular-nums text-[var(--sp-muted)]">{duty.from}–{duty.to}</span>
      )}
      {duty.marker && <span className="text-xs text-[var(--sp-muted)]">({duty.marker})</span>}
      {teachesSubject && (
        <span className="whitespace-nowrap text-xs text-amber-700 dark:text-amber-400">own subject</span>
      )}
      {!!clashes.length && (
        <span
          className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-rose-600 dark:text-rose-400"
          title={clashText(clashes)}
        >
          <AlertTriangle className="h-3 w-3" />
          {clashes.length} lesson{clashes.length === 1 ? '' : 's'}
        </span>
      )}
      {canEdit && (
        <button
          type="button" onClick={onRemove} aria-label={`Take ${duty.teacherName} off this paper`}
          className="rounded p-0.5 text-[var(--sp-muted)] hover:bg-[var(--sp-line)] hover:text-rose-600"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
}

/** "Form 3B Physics (periods 2, 3)" — the lesson, never just "busy". */
function clashText(clashes: DutyClash[]): string {
  return clashes
    .map((c) => {
      const what = `${(c.slot.classes ?? []).join(', ')} ${c.subject}`.trim() || 'a lesson';
      return `${what} (period${c.periods.length === 1 ? '' : 's'} ${c.periods.join(', ')})`;
    })
    .join('; ');
}

/** Why this person is not offered — one line, naming the actual cost. */
/**
 * Reasons in the order that decides the answer, not the order they are found.
 * Phase comes first because it is the one that is not about this window at
 * all — "teaches primary" tells the exam officer they are looking at the
 * wrong half of the staff list, and the rest is detail.
 */
function whyNot(a: DutyAvailability, phases: ReadonlySet<SchoolPhase>): string {
  if (a.wrongPhase) return `teaches ${[...phases].map((p) => (p === 'islamic' ? 'in the Islamic Department' : p)).join(' and ')}`;
  if (a.elsewhere) {
    return `already on ${a.elsewhere.paper.code} ${a.elsewhere.paper.subject}, ${a.elsewhere.duty.from}–${a.elsewhere.duty.to}`;
  }
  return clashText(a.lessons);
}

/* ── who is free to do it ─────────────────────────────────────────── */

function Picker({
  role, paper, paperPhase, papers, people, tally, slots, periods, teachesPaper, internal, hodIds, onPick, onClose,
}: {
  role: DutyRole;
  paper: ExamPaperDuties;
  paperPhase: SchoolPhase | null;
  papers: ExamPaperDuties[];
  people: Person[];
  tally: Tally;
  slots: TimetableSlot[];
  periods: PeriodTimes[];
  teachesPaper: (teacherId: string, paper: ExamPaperDuties) => boolean;
  internal: boolean;
  hodIds: ReadonlySet<string>;
  onPick: (person: Person) => void;
  onClose: () => void;
}) {
  const [find, setFind] = useState('');
  const [showBusy, setShowBusy] = useState(false);

  /* A key-time supervisor's window is not the paper's, so availability is
     judged on the window this NEW duty would actually carry — the same one
     `add` is about to give them. */
  const window = useMemo(() => {
    const like = paper.duties.find((d) => d.role === role);
    return { from: like?.from || paper.start, to: like?.to || paper.end };
  }, [paper, role]);

  const { free, busy } = useMemo(() => {
    const load = new Map(tally.map((t) => [t.teacherId, t.duties]));
    const needle = find.trim().toLowerCase();
    const rows = people
      .filter((t) => t.name.toLowerCase().includes(needle))
      .map((t) => ({
        person: t,
        duties: load.get(t.id) ?? 0,
        availability: availabilityFor(t.id, paper, window, papers, slots, periods, teachesPaper,
          { paper: paperPhase, teaches: t.phases }),
        day: dayLoad(t.id, paper.date, slots, periods, papers),
        hod: internal && hodIds.has(t.id),
      }))
      /* Already named on this paper: never offered, on either list — adding
         them twice is not a judgement call. */
      .filter((r) => !r.availability.alreadyOn);
    // The lightest load first, as cover does (rule 5): the fewest lessons and
    // duties that day, then the fewest duties across the series.
    const order = (a: typeof rows[number], b: typeof rows[number]) =>
      (a.day.lessons + a.day.duties) - (b.day.lessons + b.day.duties)
      || a.duties - b.duties
      || a.person.name.localeCompare(b.person.name);
    // A head of department is never suggested for the school's own exams;
    // the exam officer can still find them behind the toggle.
    return {
      free: rows.filter((r) => isFree(r.availability) && !r.hod).sort(order),
      busy: rows.filter((r) => !isFree(r.availability) || r.hod).sort(order),
    };
  }, [people, find, tally, paper, paperPhase, papers, window, slots, periods, teachesPaper, internal, hodIds]);

  const list = showBusy ? [...free, ...busy] : free;

  return (
    <div className="mt-3 rounded-xl border border-[var(--sp-line)] bg-[var(--sp-paper)] p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink dark:text-slate-100">
          <UserPlus className="h-4 w-4" />
          Add {role === 'keyTime' ? 'a key-time supervisor' : 'an invigilator'} — {paper.code}
          <span className="font-normal text-[var(--sp-muted)]">{window.from}–{window.to}</span>
          {/* Rule 4: say which half of the staff the list is drawn from, so a
              short list reads as a filter rather than as an empty school. */}
          {paperPhase && (
            <span className="font-normal text-[var(--sp-muted)]">· {paperPhase} staff</span>
          )}
        </p>
        <button type="button" onClick={onClose} className="text-sm text-[var(--sp-muted)] hover:underline">Cancel</button>
      </div>
      <input
        value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find a teacher"
        className={glassFieldClass('mb-2')}
      />
      <ul className="max-h-64 overflow-y-auto rounded-lg border border-[var(--sp-line)] bg-[var(--sp-card)]">
        {list.map((r) => {
          const free_ = isFree(r.availability) && !r.hod;
          return (
            <li key={r.person.id} className="border-b border-[var(--sp-line)]/60 last:border-0">
              <button
                type="button" onClick={() => onPick(r.person)}
                className="flex w-full flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-3 py-2 text-left hover:bg-[var(--sp-paper)]"
              >
                <span className={`text-sm font-semibold ${free_ ? 'text-ink dark:text-slate-100' : 'text-[var(--sp-muted)]'}`}>
                  {r.person.name}
                </span>
                <span className="text-xs text-[var(--sp-muted)]">
                  {r.day.lessons} lesson{r.day.lessons === 1 ? '' : 's'}
                  {r.day.duties ? ` + ${r.day.duties} dut${r.day.duties === 1 ? 'y' : 'ies'}` : ''} that day
                  {' · '}{r.duties} dut{r.duties === 1 ? 'y' : 'ies'} so far
                  {r.availability.ownSubject && (
                    <span className="text-amber-700 dark:text-amber-400"> · teaches this subject</span>
                  )}
                  {free_
                    ? <span className="text-emerald-700 dark:text-emerald-400"> · free then</span>
                    : <span className="text-rose-600 dark:text-rose-400"> · {r.hod ? 'head of department' : whyNot(r.availability, r.person.phases)}</span>}
                </span>
              </button>
            </li>
          );
        })}
        {!list.length && (
          <li className="px-3 py-4 text-center text-sm text-[var(--sp-muted)]">
            {find.trim()
              ? 'Nobody free by that name.'
              : paperPhase
                ? `No ${paperPhase} teacher is free for this window.`
                : 'Nobody is free for this window.'}
          </li>
        )}
      </ul>
      {/* Rules 2 and 4 are the school's own: the exam officer may still
          choose somebody who is teaching, or somebody from the other half of
          the school, and is told what it costs either way. */}
      {!!busy.length && (
        <button
          type="button" onClick={() => setShowBusy((v) => !v)}
          className="mt-2 text-xs font-semibold text-primary hover:underline"
        >
          {showBusy
            ? `Show only the ${free.length} who are free`
            : `Also show ${busy.length} who are teaching, already on duty${
              busy.some((r) => r.availability.wrongPhase) ? ', in the other part of the school' : ''}${
              busy.some((r) => r.hod) ? ' or heads of department' : ''}`}
        </button>
      )}
    </div>
  );
}

/* ── the load ─────────────────────────────────────────────────────── */

function Load({ tally, internal }: { tally: Tally; internal: boolean }) {
  const [all, setAll] = useState(false);
  const rows = all ? tally : tally.slice(0, 8);
  return (
    <SoftCard pad="">
      <PanelHeader
        icon={Users} title="Who is doing how much"
        subtitle="Across the whole series. A duty is a SESSION: two papers sat together in one room count once, and the hours are the time actually stood, not the papers added up"
        right={tally.length > 8 ? (
          <button type="button" className="text-sm font-semibold text-primary hover:underline" onClick={() => setAll((v) => !v)}>
            {all ? 'Show the busiest' : `All ${tally.length}`}
          </button>
        ) : undefined}
      />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--sp-line)] text-left text-[11px] uppercase tracking-wide text-[var(--sp-muted)]">
              <th className="px-4 py-2 font-semibold">Teacher</th>
              <th className="px-3 py-2 text-right font-semibold">Duties</th>
              <th className="px-3 py-2 text-right font-semibold">Hours</th>
              {!internal && <th className="px-3 py-2 text-right font-semibold">FCS</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.teacherId} className="border-b border-[var(--sp-line)]/60 last:border-0">
                <td className="px-4 py-2 font-semibold text-ink dark:text-slate-100">{t.teacherName}</td>
                <td className="px-3 py-2 text-right tabular-nums text-[var(--sp-body)]">{t.duties}</td>
                <td className="px-3 py-2 text-right tabular-nums text-[var(--sp-body)]">{(t.minutes / 60).toFixed(1)}</td>
                {!internal && (
                  <td className={`px-3 py-2 text-right tabular-nums ${t.keyTime ? ROLE_ICON.keyTime : 'text-[var(--sp-muted)]'}`}>
                    {t.keyTime || '—'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SoftCard>
  );
}
